import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAlerts } from '@/redux/alerts/alertsSlice';
import { ALERT_LEVEL_CONFIG } from '@/views/commandCenter/config';
import { formatDateTimeStr, renderTextStr } from '@/utils/renderUtils';

function alertKey(item) {
  if (item.id != null && item.id !== '') return String(item.id);
  return `${item.customer_name || ''}-${item.updated_at || ''}-${item.alert_type || ''}`;
}

function ServiceAlert({ storeId }) {
  const dispatch = useDispatch();
  const alertsObj = useSelector((state) => state.alerts.alerts);
  const nursing = alertsObj?.nursing || {};
  const postpartumService = alertsObj?.postpartumService || {};
  const scrollRef1 = useRef(null);
  const scrollRef2 = useRef(null);
  const [paused, setPaused] = useState({ nursing: false, recovery: false });
  const posRef1 = useRef(0);
  const posRef2 = useRef(0);
  const rafRef1 = useRef(null);
  const rafRef2 = useRef(null);
  const [open, setOpen] = useState(null);

  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';

  useEffect(() => {
    if (!selectedStoreId) return undefined;
    const query = { store_id: selectedStoreId };
    const pull = () => {
      dispatch(fetchAlerts({ serviceType: 'nursing', query })).unwrap().catch(() => {});
      dispatch(fetchAlerts({ serviceType: 'postpartumService', query })).unwrap().catch(() => {});
    };
    pull();
    const timer = setInterval(pull, 30000);
    return () => clearInterval(timer);
  }, [dispatch, selectedStoreId]);

  const nursingList = useMemo(() => {
    const queryStore = String(nursing.query?.store_id ?? '');
    if (nursing.fetching || queryStore !== selectedStoreId) return [];
    return Array.isArray(nursing.list) ? nursing.list : [];
  }, [nursing, selectedStoreId]);

  const postpartumList = useMemo(() => {
    const queryStore = String(postpartumService.query?.store_id ?? '');
    if (postpartumService.fetching || queryStore !== selectedStoreId) return [];
    return Array.isArray(postpartumService.list) ? postpartumService.list : [];
  }, [postpartumService, selectedStoreId]);

  useEffect(() => {
    posRef1.current = 0;
    if (scrollRef1.current) {
      scrollRef1.current.style.transform = 'translateY(0px)';
    }
  }, [nursingList]);

  useEffect(() => {
    posRef2.current = 0;
    if (scrollRef2.current) {
      scrollRef2.current.style.transform = 'translateY(0px)';
    }
  }, [postpartumList]);

  useEffect(() => {
    const el = scrollRef1.current;
    if (!el) return undefined;
    const halfHeight = el.scrollHeight / 2;
    const speed = 0.15;
    const animate = () => {
      if (!paused.nursing && !open && halfHeight > 0) {
        posRef1.current += speed;
        if (posRef1.current >= halfHeight) {
          posRef1.current -= halfHeight;
        }
        el.style.transform = `translateY(${-posRef1.current}px)`;
      }
      rafRef1.current = requestAnimationFrame(animate);
    };
    rafRef1.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef1.current);
  }, [paused.nursing, open, nursingList]);

  useEffect(() => {
    const el = scrollRef2.current;
    if (!el) return undefined;
    const halfHeight = el.scrollHeight / 2;
    const speed = 0.15;
    const animate = () => {
      if (!paused.recovery && !open && halfHeight > 0) {
        posRef2.current += speed;
        if (posRef2.current >= halfHeight) {
          posRef2.current -= halfHeight;
        }
        el.style.transform = `translateY(${-posRef2.current}px)`;
      }
      rafRef2.current = requestAnimationFrame(animate);
    };
    rafRef2.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef2.current);
  }, [paused.recovery, open, postpartumList]);

  const renderAlertItem = (side) => (item, idx) => {
    const raw = item.alert_level;
    const levelNum = Number(raw && typeof raw === 'object' ? raw.value : raw);
    const levelText = (Number.isFinite(levelNum) && ALERT_LEVEL_CONFIG[levelNum])
      ? ALERT_LEVEL_CONFIG[levelNum].text
      : '--';
    const statusText = item.status || '--';
    return (
      <div
        className="sa-item"
        key={`${alertKey(item)}-${idx}`}
        onClick={() => setOpen({ side, key: alertKey(item) })}
      >
        <span className={`sa-level sa-${levelText}`}>{`等级:${levelText}`}</span>
        <span className="sa-text">{`客户:${renderTextStr(item.customer_name)}`}</span>
        <span className={`sa-status sa-${statusText}`}>{statusText}</span>
        <span className="sa-text">{`类型:${renderTextStr(item.alert_type)}`}</span>
        <span className="sa-time">{`时间:${formatDateTimeStr(item.updated_at) || '--'}`}</span>
      </div>
    );
  };

  const openList = open?.side === 'recovery' ? postpartumList : nursingList;
  const openAlert = open ? (openList.find((item) => alertKey(item) === open.key) || null) : null;
  const openLevelRaw = openAlert?.alert_level;
  const openLevelNum = Number(openLevelRaw && typeof openLevelRaw === 'object' ? openLevelRaw.value : openLevelRaw);
  const openLevelText = (Number.isFinite(openLevelNum) && ALERT_LEVEL_CONFIG[openLevelNum])
    ? ALERT_LEVEL_CONFIG[openLevelNum].text
    : '--';

  return (
    <div className="service-alert-card">
      <div className="sa-shine"></div>
      <div className="sa-col">
        <div className="sa-col-title">护理预警</div>
        <div
          className="sa-scroll-wrap"
          onMouseEnter={() => setPaused((p) => ({ ...p, nursing: true }))}
          onMouseLeave={() => setPaused((p) => ({ ...p, nursing: false }))}
        >
          <div className="sa-scroll" ref={scrollRef1}>
            {nursingList.map(renderAlertItem('nursing'))}
            {nursingList.length > 0 ? nursingList.map((item, idx) => renderAlertItem('nursing')(item, `dup-${idx}`)) : null}
          </div>
        </div>
      </div>
      <div className="sa-col">
        <div className="sa-col-title">产康预警</div>
        <div
          className="sa-scroll-wrap"
          onMouseEnter={() => setPaused((p) => ({ ...p, recovery: true }))}
          onMouseLeave={() => setPaused((p) => ({ ...p, recovery: false }))}
        >
          <div className="sa-scroll" ref={scrollRef2}>
            {postpartumList.map(renderAlertItem('recovery'))}
            {postpartumList.length > 0 ? postpartumList.map((item, idx) => renderAlertItem('recovery')(item, `dup-${idx}`)) : null}
          </div>
        </div>
      </div>
      {openAlert && createPortal(
        <div className="sa-modal-overlay" onClick={() => setOpen(null)}>
          <div className="sa-modal sa-modal-blue sa-modal-edit" onClick={(e) => e.stopPropagation()}>
            <div className="sa-side">
              <div className="sa-side-title">{open.side === 'recovery' ? '产康预警' : '护理预警'}</div>
              <div className="sa-side-scroll-wrap">
                <div className="sa-side-scroll">
                  {openList.map((item) => {
                    const raw = item.alert_level;
                    const levelNum = Number(raw && typeof raw === 'object' ? raw.value : raw);
                    const levelText = (Number.isFinite(levelNum) && ALERT_LEVEL_CONFIG[levelNum])
                      ? ALERT_LEVEL_CONFIG[levelNum].text
                      : '--';
                    return (
                      <div
                        key={alertKey(item)}
                        className={`sa-side-item${alertKey(item) === open.key ? ' active' : ''}`}
                        onClick={() => setOpen({ side: open.side, key: alertKey(item) })}
                      >
                        <span className="sa-side-name">{renderTextStr(item.customer_name)}</span>
                        <span className={`sa-level sa-${levelText}`}>{levelText}</span>
                        <span className={`sa-status sa-${item.status || ''}`}>{item.status || '--'}</span>
                        <span className="sa-side-type">{`类型：${renderTextStr(item.alert_type)}`}</span>
                        <span className="sa-side-time">{formatDateTimeStr(item.updated_at) || '--'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="sa-main">
              <div className="sa-modal-header">
                <div className="sa-modal-title-row">
                  <span className="sa-edit-title">{renderTextStr(openAlert.customer_name)}</span>
                  <span className={`sa-status sa-${openAlert.status || ''}`}>{openAlert.status || '--'}</span>
                </div>
                <button type="button" className="sa-modal-close" onClick={() => setOpen(null)}>×</button>
              </div>
              <div className="sa-modal-body">
                <div className="sa-edit-info-row">
                  <span className="sa-edit-info-chip">{`等级：${openLevelText}`}</span>
                  <span className="sa-edit-info-chip">{`类型：${renderTextStr(openAlert.alert_type)}`}</span>
                  <span className="sa-edit-info-chip">{`时间：${formatDateTimeStr(openAlert.updated_at) || '--'}`}</span>
                </div>
                <div className="sa-edit-field">
                  <label className="sa-edit-label">说明</label>
                  <div className="sa-edit-info-chip">{renderTextStr(openAlert.issue_description || openAlert.remark)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default ServiceAlert;
